import { BuilderBlocks, BuilderElement, withChildren } from '@builder.io/react';
import { type ReactNode } from 'react';
import Container from '../Container/Container';
import { TagList } from '@/types/article';
import { Tags } from '@/components/ui/Tag/Tag';
import Share from '@/components/ui/Share/Share';

interface SectionProps {
  altBackground?: boolean;
  builderBlock?: BuilderElement;
  children?: ReactNode;
  className?: string;
  heading?: string;
  id?: string | undefined;
  isPageTitle?: boolean;
  noContainer?: boolean;
  noVerticalPadding?: boolean;
  isArticleContent?: boolean;
  builderState?: {
    content?: {
      data?: {
        tags?: TagList;
      };
    };
  };
}

const Section = ({
  altBackground = false,
  builderBlock,
  children,
  className = '',
  heading,
  id,
  isPageTitle = false,
  noContainer = false,
  noVerticalPadding = false,
  isArticleContent = false,
  builderState,
}: SectionProps) => {
  const bgStyle = altBackground ? 'dark bg-secondary my-4' : 'light';
  const sectionClasses = ['flex', 'w-full', className, 'group', bgStyle];
  const titleStyle = 'md:max-w-[60%]';
  const headingStyle = 'md:max-w-[60%]';

  if (!noVerticalPadding && !altBackground) {
    sectionClasses.push('py-regular');
  }

  if (!noVerticalPadding && altBackground) {
    sectionClasses.push('py-8');
  }

  const Heading = () => (
    <>
      {heading && isPageTitle && <h1 className={titleStyle}>{heading}</h1>}
      {heading && !isPageTitle && <h2 className={headingStyle}>{heading}</h2>}
    </>
  );

  const Children = () => {
    if (!builderBlock && !children) return null;
    if (!builderBlock && children) return children;

    if (builderBlock) {
      return (
        <BuilderBlocks parentElementId={builderBlock.id} blocks={builderBlock.children} dataPath="this.children" />
      );
    }
  };

  const tags = builderState?.content?.data?.tags || [];

  const Extra = () => {
    return (
      <>
        {tags.length > 0 && <Tags tags={tags} />}
        <Share />
      </>
    );
  };

  return (
    <section id={id} className={sectionClasses.filter(Boolean).join(' ')}>
      {noContainer ? (
        <div className="w-full">
          <Heading />
          <Children />
        </div>
      ) : (
        <Container>
          <Heading />
          <Children />
          {isArticleContent && <Extra />}
        </Container>
      )}
    </section>
  );
};

export default withChildren(Section);
